import React, { useState, useEffect } from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField, Alert } from '@mui/material';
import { useAuth } from '../context/AuthContext';
import authService from '../services/authService';

interface Props {
  open: boolean;
  onClose: () => void;
}

const normalizePhone = (s: string) => {
  let v = (s || '').replace(/[^0-9+]/g, '');
  if (!v) return '';
  if (v.startsWith('00')) v = '+' + v.slice(2);
  if (v.startsWith('0') && !v.startsWith('00')) v = '+49' + v.slice(1);
  if (!v.startsWith('+') && /^[0-9]+$/.test(v)) {
    if (v.length === 10 || v.length === 11) v = '+49' + v.slice(v.length - (v.length - 1));
  }
  return v;
};

// Non-JSX version to avoid parser issues
const WhatsAppNumberDialog: React.FC<Props> = ({ open, onClose }) => {
  const { user, token, refreshProfile } = useAuth();
  const [value, setValue] = useState(user?.event || '');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  useEffect(() => setValue(user?.event || ''), [user]);

  const handleSave = async () => {
    setError(null); setSuccess(null);
    if (!token) { setError('Nicht authentifiziert'); return; }
    const normalized = normalizePhone(value || '');
    if (normalized && normalized.length < 9) { setError('Ungültige Telefonnummer'); return; }
    setLoading(true);
    try {
      const res = await authService.updateUser(token, { event: normalized });
      if (res?.success === false) {
        setError(res.message || 'Speichern fehlgeschlagen');
      } else {
        setSuccess('Nummer gespeichert');
        await refreshProfile();
        setTimeout(() => onClose(), 800);
      }
    } catch (e: any) {
      setError(e?.error || e?.message || 'Fehler');
    } finally {
      setLoading(false);
    }
  };

  const children: any[] = [];
  children.push(React.createElement(DialogTitle, { key: 'title' }, 'WhatsApp-Nummer'));
  const contentChildren: any[] = [];
  if (error) contentChildren.push(React.createElement(Alert, { key: 'err', severity: 'error', sx: { mb: 2 } }, error));
  if (success) contentChildren.push(React.createElement(Alert, { key: 'ok', severity: 'success', sx: { mb: 2 } }, success));
  contentChildren.push(React.createElement(TextField, { key: 'tf', label: 'Telefonnummer', fullWidth: true, value, onChange: (e: any) => setValue(e.target.value) }));
  children.push(React.createElement(DialogContent, { key: 'content' }, ...contentChildren));
  const actions = React.createElement(DialogActions, { key: 'actions' },
    React.createElement(Button, { onClick: onClose }, 'Abbrechen'),
    React.createElement(Button, { variant: 'contained', onClick: handleSave, disabled: loading }, 'Speichern')
  );
  children.push(actions);

  return React.createElement(Dialog, { open, onClose, fullWidth: true, maxWidth: 'sm' }, ...children);
};

export default WhatsAppNumberDialog;
